import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Users, BookOpen, Sparkles, XCircle, RotateCcw, Package, Plus, HandHeart, MessageCircle, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import PaymentMethodDialog from "@/components/PaymentMethodDialog";

function fmt(d) {
    return new Date(d).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
}
function fmtDay(ts) {
    return new Date(ts * 1000).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function DashboardPage() {
    const { user, refresh: refreshUser } = useAuth();
    const [bookings, setBookings] = useState([]);
    const [regs, setRegs] = useState([]);
    const [sub, setSub] = useState(null);
    const [packs, setPacks] = useState([]);
    const [extras, setExtras] = useState({});
    const [dialog, setDialog] = useState(null);
    const [testimonials, setTestimonials] = useState([]);
    const [testiForm, setTestiForm] = useState({ rating: 5, content: "", display_name: "" });
    const [testiBusy, setTestiBusy] = useState(false);

    const load = () => {
        http.get("/bookings/mine").then((r) => setBookings(r.data));
        http.get("/registrations/mine").then((r) => setRegs(r.data));
        http.get("/subscription/mine").then((r) => setSub(r.data));
        http.get("/packs/mine").then((r) => setPacks(r.data)).catch(() => {});
        http.get("/extras").then((r) => setExtras(r.data)).catch(() => {});
        http.get("/testimonials/mine").then((r) => setTestimonials(r.data)).catch(() => {});
    };

    const submitTestimonial = async () => {
        const content = testiForm.content.trim();
        if (content.length < 20) {
            toast.error("Le témoignage doit faire au moins 20 caractères.");
            return;
        }
        setTestiBusy(true);
        try {
            await http.post("/testimonials", {
                rating: testiForm.rating,
                content,
                display_name: testiForm.display_name.trim() || null,
            });
            toast.success("Merci pour votre témoignage ✨ Il sera publié après validation.");
            setTestiForm({ rating: 5, content: "", display_name: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setTestiBusy(false);
        }
    };

    const buyExtra = (key) => {
        const ex = extras[key];
        if (!ex) return;
        setDialog({
            kind: "extra",
            extra_key: key,
            item_name: ex.name,
            amount: ex.amount,
            origin_url: window.location.origin,
        });
    };

    const extraStripeCheckout = async () => {
        const { data } = await http.post("/extras/purchase", {
            extra_key: dialog.extra_key,
            origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
    };

    useEffect(() => { load(); }, []);

    const cancel = async () => {
        try {
            await http.post("/subscription/cancel");
            toast.success("Abonnement annulé à la fin de la période");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };
    const resume = async () => {
        try {
            await http.post("/subscription/resume");
            toast.success("Abonnement réactivé");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const tier = user?.active_subscription;
    const isAdmin = user?.role === "admin";
    const hasCommunityAccess = Boolean(tier) || isAdmin;

    return (
        <div className="max-w-6xl mx-auto px-6 py-16" data-testid="dashboard-page">
            <div className="mb-12">
                <div className="text-sm tracking-[0.25em] uppercase text-primary mb-4 font-medium">Mon espace</div>
                <h1 className="font-serif text-5xl">Bonjour {user?.name}</h1>
                <div className="mt-4 flex flex-wrap gap-3 items-center">
                    {tier ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-secondary/20 text-secondary-foreground px-4 py-1.5 text-sm" data-testid="subscription-badge">
                            <Sparkles className="w-3.5 h-3.5 text-secondary" />
                            Abonnement <strong className="capitalize">{tier}</strong> actif
                        </span>
                    ) : isAdmin ? (
                        <span className="inline-flex items-center gap-2 rounded-full bg-primary/15 text-primary px-4 py-1.5 text-sm" data-testid="admin-badge">
                            <Sparkles className="w-3.5 h-3.5" />
                            Accès admin
                        </span>
                    ) : (
                        <Link to="/abonnements"><Button variant="outline" className="rounded-full" data-testid="cta-subscribe">Devenir abonné</Button></Link>
                    )}
                    {hasCommunityAccess && <Link to="/communaute"><Button className="rounded-full bg-primary text-primary-foreground" data-testid="cta-community"><Users className="w-4 h-4 mr-2" /> Communauté</Button></Link>}
                </div>
            </div>

            {sub?.active && (
                <section className="rounded-3xl border border-border/60 bg-card p-8 mb-8" data-testid="sub-management">                    <h2 className="font-serif text-2xl mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-secondary" /> Mon abonnement</h2>
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Formule</div>
                            <div className="capitalize font-medium">{sub.lookup_key?.replace(/_/g, " ")}</div>
                        </div>
                        <div>
                            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">État</div>
                            <div className={sub.cancel_at_period_end ? "text-destructive" : "text-primary"}>
                                {sub.cancel_at_period_end ? "Se termine bientôt" : "Actif"}
                            </div>
                        </div>
                        {sub.current_period_end && (
                            <div>
                                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                                    {sub.cancel_at_period_end ? "Fin d'accès" : "Prochain paiement"}
                                </div>
                                <div>{fmtDay(sub.current_period_end)}</div>
                            </div>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <Link to="/abonnements">
                            <Button variant="outline" className="rounded-full" data-testid="change-plan-btn">Changer de formule</Button>
                        </Link>
                        {sub.cancel_at_period_end ? (
                            <Button onClick={resume} className="rounded-full bg-primary text-primary-foreground" data-testid="resume-sub-btn">
                                <RotateCcw className="w-4 h-4 mr-2" /> Réactiver
                            </Button>
                        ) : (
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button variant="outline" className="rounded-full text-destructive border-destructive/40" data-testid="cancel-sub-btn">
                                        <XCircle className="w-4 h-4 mr-2" /> Annuler l'abonnement
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle className="font-serif">Annuler l'abonnement ?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            Vous continuerez à accéder à vos avantages jusqu'au {sub.current_period_end && fmtDay(sub.current_period_end)}. Aucun montant ne vous sera prélevé ensuite.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel>Garder</AlertDialogCancel>
                                        <AlertDialogAction onClick={cancel} data-testid="confirm-cancel-sub">Annuler l'abonnement</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        )}
                    </div>
                </section>
            )}

            {sub?.active && (sub?.lookup_key?.includes("accompagnement") || ["accompagnement_essentiel", "accompagnement_profond"].includes(user?.active_subscription)) && Object.keys(extras).length > 0 && (
                <section className="rounded-3xl border border-primary/30 bg-primary/5 p-8 mb-8" data-testid="dashboard-extras">
                    <h2 className="font-serif text-2xl mb-2 flex items-center gap-2"><Plus className="w-5 h-5 text-primary" /> Ajouter à mon compte</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                        En tant qu'abonné accompagnement, bénéficiez d'un tarif préférentiel sur des séances additionnelles.
                        À réserver dans les 30 jours suivant l'achat. Toutes les séances additionnelles doivent être consommées avant de pouvoir mettre fin à votre accompagnement.
                    </p>
                    <div className="grid md:grid-cols-2 gap-4">
                        {Object.entries(extras).map(([key, ex]) => {
                            const Icon = ex.session_type_id === "reiki" ? HandHeart : MessageCircle;
                            const originalPrice = ex.session_type_id === "reiki" ? 80 : 40;
                            return (
                                <button
                                    key={key}
                                    onClick={() => buyExtra(key)}
                                    className="text-left rounded-2xl border border-border/60 bg-card p-5 hover:border-primary/50 hover:shadow-md transition-all"
                                    data-testid={`buy-extra-${key}`}
                                >
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="p-2 rounded-full bg-primary/10 shrink-0"><Icon className="w-4 h-4 text-primary" /></div>
                                        <div className="flex-1">
                                            <div className="font-medium">{ex.name}</div>
                                            <div className="text-xs text-muted-foreground">Valable 30 jours</div>
                                        </div>
                                        <Plus className="w-4 h-4 text-primary" />
                                    </div>
                                    <div className="flex items-baseline gap-2">
                                        <span className="font-serif text-2xl">{(ex.amount / 100).toFixed(0)}€</span>
                                        <span className="text-xs text-muted-foreground line-through">{originalPrice}€</span>
                                    </div>
                                    <div className="text-xs text-primary mt-2">J'ajoute à mon compte</div>
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            <div className="grid md:grid-cols-2 gap-8">
                {packs.filter((p) => !p.is_expired && p.sessions_remaining > 0).length > 0 && (
                    <section className="md:col-span-2 rounded-3xl border border-secondary/40 bg-secondary/5 p-8" data-testid="dashboard-packs">
                        <h2 className="font-serif text-2xl mb-6 flex items-center gap-2"><Package className="w-5 h-5 text-secondary" /> Mes programmes</h2>
                        <div className="grid md:grid-cols-2 gap-4">
                            {packs.filter((p) => !p.is_expired && p.sessions_remaining > 0).map((p) => (
                                <div key={p.id} className="flex justify-between items-center bg-card rounded-2xl border border-border/40 p-4">
                                    <div>
                                        <div className="font-medium">{p.program_name}</div>
                                        <div className="text-xs text-muted-foreground">
                                            Valable jusqu'au {p.expires_at ? new Date(p.expires_at).toLocaleDateString("fr-FR") : "—"}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-serif text-3xl">{p.sessions_remaining}<span className="text-lg text-muted-foreground">/{p.sessions_total}</span></div>
                                        <div className="text-xs text-muted-foreground">séances restantes</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
                <section className="rounded-3xl border border-border/60 bg-card p-8" data-testid="my-bookings">
                    <h2 className="font-serif text-2xl mb-6 flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" /> Mes séances</h2>
                    {bookings.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Aucune séance réservée.</p>
                    ) : (
                        <ul className="space-y-4">
                            {bookings.map((b) => (
                                <li key={b.id} className="flex justify-between items-start border-b border-border/40 pb-4 last:border-0">
                                    <div>
                                        <div className="font-medium">{b.session_name}</div>
                                        <div className="text-xs text-muted-foreground">{fmt(b.slot_datetime)}</div>
                                    </div>
                                    <span className={`text-xs px-2.5 py-1 rounded-full ${
                                        b.status === "confirmed" ? "bg-primary/15 text-primary" :
                                        b.status === "cancelled" ? "bg-destructive/15 text-destructive" :
                                        "bg-muted text-muted-foreground"
                                    }`} data-testid={`booking-status-${b.id}`}>
                                        {b.status === "confirmed" ? "confirmée" :
                                         b.status === "cancelled" ? "annulée" : "en attente"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="rounded-3xl border border-border/60 bg-card p-8" data-testid="my-events">
                    <h2 className="font-serif text-2xl mb-6 flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> Mes événements</h2>
                    {regs.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Aucune inscription.</p>
                    ) : (
                        <ul className="space-y-4">
                            {regs.map((r) => (
                                <li key={r.id} className="flex justify-between items-start border-b border-border/40 pb-4 last:border-0">
                                    <div>
                                        <div className="font-medium">{r.event_title}</div>
                                        <div className="text-xs text-muted-foreground">{fmt(r.event_starts_at)}</div>
                                    </div>
                                    <span className={`text-xs px-2.5 py-1 rounded-full ${
                                        r.status === "confirmed" ? "bg-primary/15 text-primary" :
                                        r.status === "cancelled" ? "bg-destructive/15 text-destructive" :
                                        "bg-muted text-muted-foreground"
                                    }`} data-testid={`reg-status-${r.id}`}>
                                        {r.status === "confirmed" ? "confirmé" :
                                         r.status === "cancelled" ? "annulé" : "en attente"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="rounded-3xl border border-secondary/40 bg-gradient-to-br from-secondary/5 to-transparent p-8" data-testid="my-testimonial-section">
                    <h2 className="font-serif text-2xl mb-2 flex items-center gap-2"><Star className="w-5 h-5 text-secondary" /> Laisser un témoignage</h2>
                    <p className="text-sm text-muted-foreground mb-6">
                        Votre partage aide d'autres personnes en chemin. Il sera relu avant d'être publié sur la page d'accueil.
                    </p>
                    <div className="grid gap-4">
                        <div>
                            <Label className="text-xs mb-2 block">Votre note</Label>
                            <div className="flex gap-1" data-testid="testi-rating">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        onClick={() => setTestiForm({ ...testiForm, rating: n })}
                                        className="p-1 hover:scale-110 transition-transform"
                                        data-testid={`testi-star-${n}`}
                                        aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                                    >
                                        <Star className={`w-7 h-7 ${n <= testiForm.rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Prénom affiché <span className="text-muted-foreground">(facultatif, sinon votre nom complet)</span></Label>
                            <Input
                                value={testiForm.display_name}
                                onChange={(e) => setTestiForm({ ...testiForm, display_name: e.target.value })}
                                placeholder={user?.name || "Ex : Marie L."}
                                className="rounded-full h-11"
                                maxLength={80}
                                data-testid="testi-display-name"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Votre témoignage</Label>
                            <Textarea
                                value={testiForm.content}
                                onChange={(e) => setTestiForm({ ...testiForm, content: e.target.value })}
                                placeholder="Partagez votre expérience, ce que vous avez ressenti, ce qui a changé pour vous…"
                                className="rounded-2xl min-h-[140px]"
                                maxLength={1500}
                                data-testid="testi-content"
                            />
                            <div className="text-xs text-muted-foreground text-right mt-1">{testiForm.content.length}/1500</div>
                        </div>
                        <Button
                            onClick={submitTestimonial}
                            disabled={testiBusy || testiForm.content.trim().length < 20}
                            className="rounded-full h-11 bg-secondary text-secondary-foreground hover:scale-[1.02] transition-transform self-start"
                            data-testid="testi-submit"
                        >
                            <Send className="w-4 h-4 mr-2" /> {testiBusy ? "Envoi…" : "Soumettre pour validation"}
                        </Button>
                    </div>

                    {testimonials.length > 0 && (
                        <div className="mt-8 pt-6 border-t border-border/40">
                            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Mes témoignages précédents</div>
                            <ul className="space-y-3">
                                {testimonials.map((t) => (
                                    <li key={t.id} className="flex items-start justify-between gap-3 text-sm" data-testid={`my-testi-${t.id}`}>
                                        <div className="flex-1">
                                            <div className="flex gap-0.5 mb-1">
                                                {[1,2,3,4,5].map((n) => (
                                                    <Star key={n} className={`w-3 h-3 ${n <= t.rating ? "fill-secondary text-secondary" : "text-muted-foreground/40"}`} />
                                                ))}
                                            </div>
                                            <p className="text-foreground/80 italic">« {t.content.slice(0, 140)}{t.content.length > 140 ? "…" : ""} »</p>
                                        </div>
                                        <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 shrink-0 ${
                                            t.status === "approved" ? "bg-primary/15 text-primary" :
                                            t.status === "rejected" ? "bg-destructive/15 text-destructive" :
                                            "bg-secondary/15 text-secondary-foreground"
                                        }`}>
                                            {t.status === "approved" ? "publié" : t.status === "rejected" ? "refusé" : "en attente"}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </section>
            </div>
            <PaymentMethodDialog
                open={!!dialog}
                onOpenChange={(o) => !o && setDialog(null)}
                item={dialog}
                stripeCheckout={extraStripeCheckout}
            />
        </div>
    );
}
